/*global angular*/

(function (angular) {
    'use strict';

    angular
        .module('zcooly.i18n', [])
        .provider('i18n', I18nProvider)
        .directive('i18n', I18nDirective);

    /**
     * The translation provider which is available for configuration in the config step.
     */
    function I18nProvider() {
        var self = this,
            config = {
                languages: ['en'],
                defaultLanguage: 'en',
                path: null,
                debug: false,
            };

        self.setLanguages = setLanguages;
        self.setDefaultLanguage = setDefaultLanguage;
        self.setPathToTranslations = setPathToTranslations;
        self.debugMode = debugMode;

        /**
         * Set an array of your available translations.
         *
         * @param {string[]} languageArray An array of two-letter language codes.
         */
        function setLanguages(languageArray) {
            if (config.debug) {
                console.log('Setting languages to: %s.', languageArray.join(', '));
            }

            config.languages = languageArray.join(',').toLowerCase().split(',');
        }

        /**
         * Set default language for the application.
         *
         * @param {string|function} defaultLanguage A two-letter language code or a callback where
         * you can return the language. The callback should also return a two-letter language code.
         */
        function setDefaultLanguage(defaultLanguage) {
            config.defaultLanguage = defaultLanguage;
        }

        /**
         * Set the path to the translation files.
         *
         * @param {string|function} path A string which is the URL to the directory where
         * the translation files are stored. It can also be a callback which will receive the
         * language code as the first and only argument, and it should return the path
         * directly to the specific JSON language file.
         */
        function setPathToTranslations(path) {
            config.path = path;
        }

        /**
         * Toggle debug mode.
         *
         * @param {boolean} isActive
         */
        function debugMode(isActive) {
            config.debug = isActive;
        }

        /**
         * Creates a new instance of the factory.
         */
        self.$get = ['$http', '$window', '$q', function ($http, $window, $q) {
            return new I18nFactory(config, $http, $window, $q);
        }];
    }

    /**
     * The translation factory which can be injected into other services, controllers, directives, ...
     *
     * @param {object} config The configuration object.
     * @param {object} $http
     * @param {object} $window
     * @param {function} $q
     */
    function I18nFactory(config, $http, $window, $q) {
        var service = {
                dictionaries: {},
                languages: [],
                language: null,
                getLanguage: getLanguage,
                get: get,
                changeLanguage: changeLanguage,
                on: onEvent,
                off: offEvent,
            },
            languageIsParsed = false,
            loadPromises = {},
            path = null,
            defaultLanguage = null,
            debug = false,
            eventHandlers = {
                change: [],
            };

        function activate() {
            service.languages = config.languages;
            defaultLanguage = config.defaultLanguage;
            path = config.path;

            angular.forEach(service.languages, function (language) {
                loadPromises[language] = null;
            });

            service.language = getLanguage();

            debug = config.debug;

            if (debug && path === null) {
                console.warn('No path set for translation files.');
            }
        }

        /**
         * Allows the user to change the current language on-the-fly.
         *
         * @param {string} language A two character language code.
         */
        function changeLanguage(language) {
            if (service.languages.indexOf(language) !== -1) {
                service.language = language;

                triggerEvent('change', [language]);
            }
        }

        /**
         * Connects an event handler callback to a certain event.
         *
         * @param {string} event
         * @param {function} callback
         */
        function onEvent(event, callback) {
            if (eventHandlers.hasOwnProperty(event)) {
                eventHandlers[event].push(callback);
            }
        }

        /**
         * Disconnects an event handler callback from a certain event.
         *
         * @param {string} event
         * @param {function|null} callback
         */
        function offEvent(event, callback) {
            if (eventHandlers.hasOwnProperty(event)) {
                if (typeof callback === 'function') {
                    /**
                     * Remove specific handlers if the handler was supplied in the callback argument.
                     */
                    for (var i = eventHandlers[event].length - 1; i >= 0; i--) {
                        if (eventHandlers[event][i] === callback) {
                            eventHandlers[event].splice(i, 1);
                        }
                    }
                } else {
                    /**
                     * Remove all the event's handlers if no handler was supplied in the callback argument.
                     */
                    eventHandlers[event] = [];
                }
            }
        }

        /**
         * Triggers an event.
         *
         * @param {string} event
         * @param {Array|any} data
         */
        function triggerEvent(event, data) {
            if (typeof data === 'undefined') {
                data = [];
            } else if (!Array.isArray(data)) {
                data = [data];
            }

            if (eventHandlers[event]) {
                for (var i = 0; i < eventHandlers[event].length; i++) {
                    eventHandlers[event][i].apply(service, data);
                }
            }
        }

        /**
         * Get the current language, and compute it if it's the first time it's requested.
         *
         * @returns {string} Returns the two-letter language code.
         */
        function getLanguage() {
            if (languageIsParsed) {
                return service.language;
            }

            var language = service.language;

            /**
             * Get default language.
             */
            if (typeof defaultLanguage === 'string') {
                language = defaultLanguage;
            } else if (typeof defaultLanguage === 'function') {
                language = defaultLanguage.apply(service, []);
            }

            var search = getSearchParams();

            /**
             * Check if the language is overridden by a GET parameter.
             */
            if (search.language && search.language.length) {
                language = search.language.substring(0, 2);
            } else if (search.lang && search.lang.length) {
                language = search.lang.substring(0, 2);
            }

            if (service.languages.indexOf(language) === -1) {
                if (debug) {
                    console.warn('Trying to use unavailable language %s.', language);
                }
                console.log('Invalid language (%s).', language);
                language = service.languages[0];
            }

            if (debug) {
                console.log('Computed the language to %s.', language);
            }

            languageIsParsed = true;

            return language.toLowerCase();
        }

        /**
         * Get search params from the active URL.
         *
         * @returns {object} Returns the search parameters as a {key: value} object.
         */
        function getSearchParams() {
            var params = {};

            if ($window.location.search && $window.location.search.startsWith('?')) {
                var search = $window.location.search.substring(1).split('&');
                for (var i = 0; i < search.length; i++) {
                    var searchParam = search[i].split('=');
                    params[searchParam[0]] = typeof searchParam[1] !== 'undefined' ? searchParam[1] : true;
                }
            }

            return params;
        }

        /**
         * Get a language's translation file path.
         *
         * @param {string} language
         */
        function getLanguagePath(language) {
            if (typeof path === 'function') {
                return path.apply(service, [language]);
            }

            if (path.endsWith('/')) {
                return path + language + '.json';
            }

            return path + '/' + language + '.json';
        }

        /**
         * Loads the language JSON file for a given language into the translation dictionary.
         *
         * @param {string} language A two-letter language code.
         */
        function loadLanguage(language) {
            var languagePath = getLanguagePath(language);

            return $q(function (resolve, reject) {
                $http({
                    method: 'GET',
                    url: languagePath,
                }).then(function (response) {
                    service.dictionaries[language] = {};

                    if (response.data) {
                        service.dictionaries[language] = response.data;
                    }

                    resolve();
                }, function () {
                    reject();
                });
            });
        }

        /**
         * Get a translation for a key. It will return a promise which is fullfilled
         * when the translation is found.
         *
         * @param {string} key
         * @param {boolean} resolveKeyOnMissing Set to true if you want the promises for missing
         * strings to be resolved in stead of rejected, and return the key as the translation.
         *
         * @returns {$q} A $q promise.
         */
        function get(key, resolveKeyOnMissing) {
            if (!service.dictionaries[service.language]) {
                if (loadPromises[service.language] === null) {
                    loadPromises[service.language] = loadLanguage(service.language);
                }

                return $q(function (resolve, reject) {
                    loadPromises[service.language].then(function () {
                        if (service.dictionaries[service.language][key]) {
                            resolve(service.dictionaries[service.language][key]);
                        } else {
                            if (debug) {
                                console.warn('Missing translation %s (%s).', key, service.language);
                            }

                            if (resolveKeyOnMissing) {
                                resolve(key);
                            } else {
                                reject(key);
                            }
                        }
                    }, function () {
                        if (debug) {
                            console.warn('Failed to load language file (%s).', service.language);
                        }

                        if (resolveKeyOnMissing) {
                            resolve(key);
                        } else {
                            reject(key);
                        }
                    });
                });
            } else {
                return $q(function (resolve, reject) {
                    if (service.dictionaries[service.language][key]) {
                        resolve(service.dictionaries[service.language][key]);
                    } else {
                        if (debug) {
                            console.warn('Missing translation %s (%s).', key, service.language);
                        }

                        if (resolveKeyOnMissing) {
                            resolve(key);
                        } else {
                            reject(key);
                        }
                    }
                });
            }
        }



        activate();

        return service;
    }

    I18nDirective.$inject = ['i18n'];

    /**
     * The base directive that will replace the content of the element with the translated content.
     *
     * @param {object} i18n
     */
    function I18nDirective(i18n) {
        var directive = {
            restrict: 'EA',
            link: link,
            scope: {
                placeholders: '='
            },
        };

        function render(scope, element, attribute, text) {
            if (text) {
                if (scope.placeholders) {
                    angular.forEach(scope.placeholders, function (value, key) {
                        text = text.replace(new RegExp('%' + key + '%', 'g'), value);
                    });
                }

                switch (attribute) {
                    case 'i18nText':
                        element.html(text);
                        break;
                    case 'i18nPlaceholder':
                        element.attr('placeholder', text);
                        break;
                    case 'i18nTitle':
                        element.attr('title', text);
                        break;
                    case 'i18nAlt':
                        element.attr('alt', text);
                        break;
                }
            }
        }

        function link(scope, element, attrs) {
            translate(scope, element, attrs);

            i18n.on('change', function () {
                translate(scope, element, attrs);
            });

            if (scope.placeholders) {
                scope.$watch('placeholders', function () {
                    translate(scope, element, attrs);
                });
            }
        }

        function translate(scope, element, attrs) {
            if (attrs.i18nText) {
                translateAttribute(scope, element, attrs, 'i18nText');
            }

            if (attrs.i18nPlaceholder) {
                translateAttribute(scope, element, attrs, 'i18nPlaceholder');
            }

            if (attrs.i18nTitle) {
                translateAttribute(scope, element, attrs, 'i18nTitle');
            }

            if (attrs.i18nAlt) {
                translateAttribute(scope, element, attrs, 'i18nAlt');
            }
        }

        function translateAttribute(scope, element, attrs, attribute) {
            i18n.get(attrs[attribute]).then(function (translation) {
                render(scope, element, attribute, translation);
            }, function () {
                render(scope, element, attribute, attrs[attribute]);
            });
        }

        return directive;
    }

})(angular);